import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { VendorFormData, getVendorFormSchema } from "./create-vendor.schema";
import BaseInput from "../../../shared/components/BaseInput";
import { useIndustrySpecialists } from "../../../industry-specialist/hooks/useIndustrySpecialists";
import BaseButton from "components/common/BaseButton";
import { useIntl } from "react-intl";
import { useNAMStates } from "pages/admin-app/states/hooks/useNAMStates";
import BaseAsyncPaginate from "pages/admin-app/shared/components/BaseAsyncPaginate";
import { loadIndustrySpecialists } from "pages/admin-app/industry-specialist/services/loadIndustrySpecialists";
import { useCountries } from "pages/admin-app/country/hooks/useCountries";
import { loadCountries } from "pages/admin-app/country/services/loadCountries";
import { loadStates } from "pages/admin-app/states/services/loadStates";
import { useEffect } from "react";

interface Props {
  onSubmit: (data: VendorFormData) => void;
  isSubmitting: boolean;
}

const CreateVendorForm = ({ onSubmit, isSubmitting }: Props) => {
  const intl = useIntl();
  const vendorFormSchema = getVendorFormSchema(intl);

  const methods = useForm({
    resolver: zodResolver(vendorFormSchema),
  });

  const { handleSubmit, control, setValue, clearErrors } = methods;
  const selectedCountry = useWatch({ control, name: "countryId" });

  useEffect(() => {
    setValue("state", "");
    clearErrors("state");
  }, [selectedCountry, setValue, clearErrors]);

  const {
    data: industrySpecialists,
    isLoading,
    isError,
    error: specialistsError,
    refetch: refetchIndustrySpecialists,
    isFetching: isFetchingSpecialists,
  } = useIndustrySpecialists({ currentPage: 1 });

  const {
    data: states,
    isLoading: isLoadingStates,
    error: statesError,
    isError: isErrorStates,
    refetch: refetchStates,
    isFetching: isFetchingStates,
  } = useNAMStates({
    countryId: selectedCountry,
    currentPage: 1,
  });

  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    error: countriesError,
    refetch: refetchCountries,
    isFetching: isFetchingCountries,
  } = useCountries({ currentPage: 1 });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="inputs-container">
        <BaseInput
          name="name"
          label={intl.formatMessage({ id: "usAdmin.name" })}
          type="text"
          containerClassName="usadmin-input-name"
        />
        <BaseInput
          name="contactName"
          label={intl.formatMessage({ id: "usAdmin.contactName.vendor" })}
          type="text"
          containerClassName="usadmin-input-contactName"
        />
        <BaseInput
          name="address"
          label={intl.formatMessage({ id: "usAdmin.address" })}
          type="text"
          containerClassName="second-column"
        />
        <div className="third-row">
          <BaseInput
            name="email"
            label={intl.formatMessage({ id: "usAdmin.email.vendor" })}
            type="text"
          />
          <BaseInput
            name="phone"
            label={intl.formatMessage({ id: "usAdmin.phone" })}
            type="text"
          />
          <BaseInput
            name="zip"
            label={intl.formatMessage({ id: "usAdmin.zip" })}
            type="text"
          />
        </div>
        <div className="third-row">
          <BaseInput
            name="city"
            label={intl.formatMessage({ id: "usAdmin.city" })}
            type="text"
          />
          <BaseAsyncPaginate
            name="state"
            label={intl.formatMessage({ id: "usAdmin.state" })}
            loadOptions={loadStates(selectedCountry)}
            data={states?.data || []}
            isLoading={isLoadingStates || isFetchingStates}
            isError={isErrorStates}
            error={statesError}
            refetch={refetchStates}
            optionMapper={(state) => ({
              value: state.abbr,
              label: `${state.name} (${state.abbr})`,
            })}
            placeholder={intl.formatMessage({ id: "usAdmin.selectState" })}
            isDisabled={!selectedCountry}
            selectedCountry={selectedCountry}
          />
          <BaseAsyncPaginate
            name="countryId"
            label={intl.formatMessage({ id: "country" })}
            loadOptions={loadCountries}
            data={countries?.data || []}
            additional={{ page: 1 }}
            isLoading={isLoadingCountries || isFetchingCountries}
            isError={isErrorCountries}
            error={countriesError}
            optionMapper={(country) => ({
              value: country.id,
              label: country.name,
            })}
            refetch={refetchCountries}
            placeholder={intl.formatMessage({ id: "usAdmin.selectCountry" })}
          />
        </div>
        <div className="second-column form-group">
          <BaseAsyncPaginate
            name="isId"
            label={intl.formatMessage({ id: "usAdmin.industrySpecialist" })}
            loadOptions={loadIndustrySpecialists}
            data={industrySpecialists?.data || []}
            optionMapper={(spec) => ({
              value: spec.id,
              label: spec.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoading || isFetchingSpecialists}
            isError={isError}
            error={specialistsError}
            refetch={refetchIndustrySpecialists}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectIndustrySpecialist",
            })}
          />
        </div>
        <BaseButton
          text={intl.formatMessage({ id: "usAdmin.create" })}
          type="submit"
          disabled={isSubmitting}
          className="first-column usadmin-form-submit-button"
          handleSubmit={() => {}}
        />
      </form>
    </FormProvider>
  );
};

export default CreateVendorForm;
